// Live "Daily Brief" engine — derives a morning summary from current workspace data.
export function buildBrief({ currentUser, events = [], tasks = [], renewals = [], documents = [] }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const isPast = (d) => d && d !== "—" && new Date(d) < today;

  const openTasks = tasks.filter((t) => t.status !== "done");
  const overdueTasks = tasks.filter((t) => t.status === "overdue" || (t.status !== "done" && isPast(t.due)));
  const highTasks = openTasks.filter((t) => t.priority === "high");
  const dueRenewals = renewals.filter((r) => r.status === "due-soon" || r.status === "overdue");
  const expiringDocs = documents.filter((d) => d.status === "expiring" || d.status === "overdue");
  const upcoming = [...events].filter((e) => new Date(e.date) >= today).sort((a, b) => a.date.localeCompare(b.date) || (a.time || "").localeCompare(b.time || ""));
  const doneCount = tasks.filter((t) => t.status === "done").length;

  const dedupe = (arr) => arr.filter((t, i, a) => a.findIndex((x) => x.id === t.id) === i);
  const priorities = dedupe([...overdueTasks, ...highTasks]);

  const firstName = (currentUser?.name || "there").split(" ")[0];
  const hour = new Date().getHours();
  const partOfDay = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
  const greeting = `Good ${partOfDay}, ${firstName}`;
  const dateLabel = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const attention = overdueTasks.length + dueRenewals.length + expiringDocs.length;
  const parts = [];
  if (overdueTasks.length) parts.push(`${overdueTasks.length} overdue task${overdueTasks.length > 1 ? "s" : ""}`);
  if (dueRenewals.length) parts.push(`${dueRenewals.length} renewal${dueRenewals.length > 1 ? "s" : ""} due`);
  if (expiringDocs.length) parts.push(`${expiringDocs.length} document${expiringDocs.length > 1 ? "s" : ""} expiring`);
  const nextEv = upcoming[0];
  const nextStr = nextEv ? ` Next up: ${nextEv.title} on ${new Date(nextEv.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}.` : "";
  const summary = attention
    ? `You have ${attention} item${attention > 1 ? "s" : ""} needing attention: ${parts.join(", ")}.${nextStr}`
    : `Everything's under control — no overdue tasks, renewals or expiring documents.${nextStr}`;

  const highlights = [];
  const overdueRen = renewals.find((r) => r.status === "overdue");
  if (overdueRen) highlights.push({ icon: "alert", tone: "critical", text: `${overdueRen.title} is overdue — action needed today.` });
  if (expiringDocs.length) highlights.push({ icon: "doc", tone: "warning", text: `${expiringDocs.length} document${expiringDocs.length > 1 ? "s" : ""} expire soon (${expiringDocs.slice(0, 2).map((d) => d.name).join(", ")}${expiringDocs.length > 2 ? "…" : ""}).` });
  if (nextEv) highlights.push({ icon: "calendar", tone: "warning", text: `${nextEv.title} — ${new Date(nextEv.date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} at ${nextEv.time}.` });
  if (doneCount) highlights.push({ icon: "check", tone: "good", text: `${doneCount} task${doneCount > 1 ? "s" : ""} completed recently — nice work.` });

  const blockers = [
    ...renewals.filter((r) => r.status === "overdue").map((r) => ({ id: r.id, title: r.title, kind: "Renewal overdue" })),
    ...expiringDocs.map((d) => ({ id: d.id, title: d.name, kind: "Document expiring" })),
  ].slice(0, 6);

  return {
    greeting, dateLabel, summary,
    highlights: highlights.slice(0, 4),
    focus: priorities.slice(0, 3).map((t) => t.title),
    agenda: upcoming.slice(0, 6),
    priorities: priorities.slice(0, 6),
    blockers,
    stats: { attention, overdueTasks: overdueTasks.length, dueRenewals: dueRenewals.length, expiringDocs: expiringDocs.length, upcoming: upcoming.length },
  };
}
